import Vue from "vue";
import Vuex from "vuex";
import orderBy from "lodash/orderBy";
import uniqBy from "lodash/uniqBy";
import flatMap from "lodash/flatMap";
import min from "lodash/min";
import max from "lodash/max";
import cloneDeep from "lodash/cloneDeep";
import countBy from "lodash/countBy";
import get from "lodash/get";

import SearchParamsDesktop from "../../components/Search/SearchParamsDesktop";
import SearchParamsMobile from "../../components/Search/SearchParamsMobile";
import MobileFilters from "../../components/Search/MobileFilters/ecoventure";
import SearchStickyNav from "../../components/Search/SearchStickyNav";
import ZbModal from "../../components/core/ZbModal";
import Responsive from "../../components/core/Responsive";
import EcoventureResultPanel from "../../components/Search/EcoventureResultPanel";
import EcoventureBudgetFilter from "../../components/Search/DesktopFilters/EcoventureBudgetFilter";
import EcoventureCategoryFilter from "../../components/Search/DesktopFilters/EcoventureCategoryFilter";
import EcoventureLengthFilter from "../../components/Search/DesktopFilters/EcoventureLengthFilter";
import EcoventureExperienceFilter from "../../components/Search/DesktopFilters/EcoventureExperienceFilter";
import EcoventureDestinationFilter from "../../components/Search/DesktopFilters/EcoventureDestinationFilter";
import EcoventureInclusionFilter from "../../components/Search/DesktopFilters/EcoventureInclusionFilter";
import EcoventureClearFiltersButton from "../../components/Search/EcoventureClearFiltersButton";
import EcoventureResultSummary from "../../components/Search/EcoventureResultSummary";
import {
  applyFilterURL,
  getPrimaryDestinationFilters,
  getBudgetFilters,
} from "../../helpers/ecoventureSearchHelpers";
import {
  searchWithDelay,
  syncURLParams,
  getBudgetsFilter,
} from "../../helpers/searchHelpers";
import { ECOVENTURE_CATEGORIES } from "../../helpers/constants";
import currencyRanges from "../../helpers/currencyRanges.json";

const algoliasearchV4 = require("algoliasearch");
const AVAILABLE_RANKINGS = ["shortest-first", "longest-first", "price"];

Vue.filter("formatPrice", (rawPrice) => {
  return rawPrice.toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
});

Vue.filter("handleExternalURL", (url) => {
  if (url.startsWith("http")) {
    return url;
  } else {
    return `http://${url}`;
  }
});

Vue.component("search-params-desktop", SearchParamsDesktop);
Vue.component("search-params-mobile", SearchParamsMobile);
Vue.component("mobile-filters", MobileFilters);
Vue.component("search-sticky-nav", SearchStickyNav);
Vue.component("zb-modal", ZbModal);
Vue.component("responsive", Responsive);
Vue.component("ecoventure-budget-filter", EcoventureBudgetFilter);
Vue.component("ecoventure-category-filter", EcoventureCategoryFilter);
Vue.component("ecoventure-length-filter", EcoventureLengthFilter);
Vue.component("ecoventure-experience-filter", EcoventureExperienceFilter);
Vue.component("ecoventure-destination-filter", EcoventureDestinationFilter);
Vue.component("ecoventure-inclusion-filter", EcoventureInclusionFilter);
Vue.component("ecoventure-clear-filters-button", EcoventureClearFiltersButton);
Vue.component("ecoventure-result-summary", EcoventureResultSummary);
Vue.component("result-panel", EcoventureResultPanel);

Vue.use(Vuex);
Vue.use({
  install(Vue) {
    const Algolia = algoliasearchV4(
      window.zubluAlgolia.applicationID,
      window.zubluAlgolia.publicAPIKey
    );
    const ecoventureIndex = Algolia.initIndex(
      window.zubluAlgolia.ecoventureIndex
    );

    Vue.prototype.$algolia = {
      instance: Algolia,
      searchAllProgrammes: function({ facetFilters, numericFilters }) {
        return new Promise((resolve, reject) => {
          ecoventureIndex
            .search("", {
              facetFilters,
              numericFilters,
              hitsPerPage: 1000,
            })
            .then(resolve)
            .catch(reject);
        });
      },
    };
  },
});

const plugin = (store) => {
  window.addEventListener("onCurrencyChanged", (e) => {
    store.commit("setCurrency", e.detail.currency);
    store.dispatch("loadStatistics");
    store.dispatch("loadProgrammes");
    applyFilterURL(store.state);
  });
};

const store = new Vuex.Store({
  state() {
    const searchParams = new URLSearchParams(window.location.search);

    let page = 1;
    if (searchParams.has("page") && searchParams.get("page") !== "1") {
      page = Number(searchParams.get("page"));

      if (Number.isNaN(page)) {
        page = 1;
      }
    }

    let ranking = "price";
    if (
      searchParams.has("sort") &&
      AVAILABLE_RANKINGS.includes(searchParams.get("sort"))
    ) {
      ranking = searchParams.get("sort");
    }

    if (searchParams.has("budgets")) {
      const budgets = getBudgetsFilter("ecoventure");
      window.zubluSearchParams.budgets = budgets;
    }

    return {
      filters: window.zubluSearchParams,
      page,
      ranking: ranking,
      isLoading: true,
      programmesResults: [],
      statistics: null,
      currency: window.topNavStore.state.currency,
    };
  },
  getters: {
    tripLengthComparer(state) {
      const tripLengths = state.filters.tripLengths.split(",").filter((x) => x);
      const weeks = [];

      if (tripLengths.includes("0")) {
        weeks.push(0);
        weeks.push(1);
      }
      if (tripLengths.includes("2")) {
        weeks.push(2);
        weeks.push(3);
      }
      if (tripLengths.includes("4")) {
        weeks.push(4);
        weeks.push(5);
        weeks.push(6);
        weeks.push(7);
      }
      if (tripLengths.includes("8")) {
        weeks.push(8);
      }

      return weeks;
    },
    secondaryFilteredResults(state, getters) {
      const tripLengths = state.filters.tripLengths.split(",").filter((x) => x);

      const facilities = state.filters.facilities.split(",").filter((x) => x);

      const experiences = state.filters.experiences.split(",").filter((x) => x);

      const categories = state.filters.categories.split(",").filter((x) => x);

      let filteredProgrammes = state.programmesResults
        .filter((programme) => {
          if (categories.length === 0) {
            return true;
          }

          return categories.includes(programme.__odyssey_category.toString());
        })
        .filter((programme) => {
          if (tripLengths.length === 0) {
            return true;
          }

          const weeks = getters.tripLengthComparer;
          let result = false;

          if (
            programme.__odyssey_category === ECOVENTURE_CATEGORIES.ECOVENTURE
          ) {
            if (
              programme.__odyssey_min_length === programme.__odyssey_max_length
            ) {
              result =
                weeks.includes(programme.__odyssey_min_length) ||
                weeks.includes(8);
            } else if (
              programme.__odyssey_min_length < programme.__odyssey_max_length
            ) {
              result =
                weeks.includes(programme.__odyssey_min_length) ||
                weeks.includes(programme.__odyssey_max_length) ||
                weeks.includes(8);
            } else if (programme.__odyssey_max_length === null) {
              if (
                programme.__odyssey_min_length >= min(weeks) &&
                programme.__odyssey_min_length <= max(weeks)
              ) {
                result = true;
              }

              if (programme.__odyssey_min_length < min(weeks)) {
                result = true;
              }
            }
          } else if (
            programme.__odyssey_category === ECOVENTURE_CATEGORIES.EXPEDITION
          ) {
            result =
              programme.__odyssey_expedition_length_in_weeks >= min(weeks) &&
              programme.__odyssey_expedition_length_in_weeks <= max(weeks);

            if (weeks.includes(8) && result === false) {
              result = programme.__odyssey_expedition_length_in_weeks > 8;
            }
          }

          return result;
        })
        .filter((programme) => {
          if (facilities.length === 0) {
            return true;
          }

          for (const inclId of facilities) {
            const hasThisIncl = programme.__odyssey_inclusions.some((_incl) => {
              return _incl.id.toString() === inclId.toString();
            });

            if (!hasThisIncl) {
              return false;
            }
          }

          return true;
        })
        .filter((programme) => {
          if (experiences.length === 0) {
            return true;
          }

          for (const expId of experiences) {
            const hasThisExp = programme.__odyssey_marine_focus.some((_exp) => {
              return _exp.id.toString() === expId;
            });

            if (!hasThisExp) {
              return false;
            }
          }

          return true;
        });

      return filteredProgrammes;
    },
    sortedResults(state, getters) {
      let sorter = ["__odyssey_price_per_week"];
      let sortdir = ["asc"];

      if (state.ranking === "shortest-first") {
        sorter = ["__shortest_first_comparer", "__odyssey_price_per_week"];
        sortdir = ["asc", "asc"];
      }
      if (state.ranking === "longest-first") {
        sorter = ["__longest_first_comparer", "__odyssey_price_per_week"];
        sortdir = ["desc", "asc"];
      }

      if (state.ranking !== "longest-first") {
        return orderBy(getters.secondaryFilteredResults, sorter, sortdir);
      } else {
        const infiniteLengthPg = getters.secondaryFilteredResults.filter(
          (programme) => {
            if (
              programme.__odyssey_category === ECOVENTURE_CATEGORIES.ECOVENTURE
            ) {
              return programme.__odyssey_max_length === null;
            }

            return false;
          }
        );

        const normalPg = getters.secondaryFilteredResults.filter(
          (programme) => {
            if (
              programme.__odyssey_category === ECOVENTURE_CATEGORIES.ECOVENTURE
            ) {
              return programme.__odyssey_max_length !== null;
            }

            return true;
          }
        );

        return orderBy(
          infiniteLengthPg,
          ["__longest_first_comparer"],
          ["desc"]
        ).concat(orderBy(normalPg, sorter, sortdir));
      }
    },
    availableDestinations(state) {
      const programmes = get(state.statistics, ["programmes"], []);

      let allCountries = uniqBy(
        flatMap(programmes, (programmes) => {
          return programmes.__odyssey_country_programme;
        }),
        (country) => country.code
      );

      allCountries = orderBy(allCountries, ["name"], ["asc"]);

      return allCountries;
    },
    availableExperiences(state) {
      let experiences = flatMap(
        state.programmesResults || [],
        (programme) => programme.__odyssey_marine_focus
      );

      experiences = uniqBy(experiences, (exp) => exp.id);

      experiences = orderBy(experiences, ["name"], ["asc"]).filter(
        (exp) => exp.filterable === true
      );

      return experiences;
    },
    availableInclusions(state) {
      let inclusions = flatMap(
        state.programmesResults || [],
        (programme) => programme.__odyssey_inclusions
      );

      inclusions = uniqBy(inclusions, (incl) => incl.id);

      inclusions = orderBy(inclusions, ["order"], ["asc"]);

      return inclusions;
    },
    availableBudgetOptions(state) {
      return get(state.statistics, ["visibleBudgets"], {});
    },
  },
  mutations: {
    setFilter(state, { name, value }) {
      state.filters[name] = value;
      applyFilterURL(state);
    },
    setFilters(state, filters) {
      state.filters = filters;
      applyFilterURL(state);
    },
    setIsLoading(state, isLoading) {
      state.isLoading = isLoading;
    },
    setProgrammesResults(state, programmesResults) {
      state.programmesResults = programmesResults;
    },
    setStatistics(state, statistics) {
      state.statistics = statistics;
    },
    setRanking(state, ranking) {
      state.ranking = ranking;

      const searchParams = new URLSearchParams(window.location.search);
      if (state.ranking !== "price") {
        searchParams.set("sort", state.ranking);
      } else {
        searchParams.delete("sort");
      }

      syncURLParams(searchParams);
    },
    setPage(state, page) {
      state.page = page;
      const searchParams = new URLSearchParams(window.location.search);

      if (state.page !== 1) {
        searchParams.set("page", state.page);
      } else {
        searchParams.delete("page");
      }

      syncURLParams(searchParams);
    },
    clearFilters(state) {
      state.filters = {
        budgets: "",
        destinations: "",
        facilities: "",
        tripLengths: "",
        categories: "",
        experiences: "",
      };
      state.page = 1;

      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete("budgets");
      searchParams.delete("destinations");
      searchParams.delete("facilities");
      searchParams.delete("tripLengths");
      searchParams.delete("categories");
      searchParams.delete("experiences");
      searchParams.delete("page");

      syncURLParams(searchParams);
    },
    setCurrency(state, value) {
      state.currency = value;
    },
  },
  actions: {
    loadProgrammes(context) {
      context.commit("setIsLoading", true);

      const facetFilters = [
        getPrimaryDestinationFilters(context.state.filters),
      ].filter((f) => f);

      const numericFilters = getBudgetFilters(context.state.filters);

      return searchWithDelay(
        this._vm.$algolia.searchAllProgrammes,
        0
      )({ facetFilters, numericFilters }).then((result) => {
        const programmes = cloneDeep(result.hits);

        for (const programme of programmes) {
          if (
            programme.__odyssey_category === ECOVENTURE_CATEGORIES.EXPEDITION
          ) {
            programme.__odyssey_expedition_length_in_weeks =
              programme.__odyssey_expedition_length / 7;
          }

          programme.__shortest_first_comparer =
            programme.__odyssey_category === ECOVENTURE_CATEGORIES.EXPEDITION
              ? programme.__odyssey_expedition_length_in_weeks
              : programme.__odyssey_min_length;

          programme.__longest_first_comparer =
            programme.__odyssey_category === ECOVENTURE_CATEGORIES.EXPEDITION
              ? programme.__odyssey_expedition_length_in_weeks
              : programme.__odyssey_max_length;
        }

        context.commit("setProgrammesResults", programmes);
        context.commit("setIsLoading", false);
      });
    },
    loadStatistics(context) {
      const facetFilters = [];

      this._vm.$algolia
        .searchAllProgrammes({
          facetFilters: [facetFilters],
          numericFilters: "__odyssey_price_per_week > 0",
        })
        .then((result) => {
          const programmes = cloneDeep(result.hits);
          const { currency } = context.state;
          const rangeInSelectedCurrency =
            currencyRanges[currency.code].ecoventure.filterRange;
          const rate = window.zubluCurrencyTable[currency.code];

          const visibleBudgets = countBy(result.hits, (hit) => {
            if (
              hit.__odyssey_price_per_week <=
              rangeInSelectedCurrency[0][1] * rate
            ) {
              return "min0";
            } else if (
              hit.__odyssey_price_per_week <=
              rangeInSelectedCurrency[500][1] * rate
            ) {
              return "min500";
            } else if (
              hit.__odyssey_price_per_week <=
              rangeInSelectedCurrency[1000][1] * rate
            ) {
              return "min1000";
            }

            return "min1500";
          });

          context.commit("setStatistics", {
            programmes,
            visibleBudgets,
          });
        });
    },
  },
  plugins: [plugin],
});

store.watch(
  (state, getters) => {
    return getters.sortedResults;
  },
  (sortedResults) => {
    const currentPage = store.state.page;
    const perPage = 12;
    const maxPages = Math.ceil(sortedResults.length / perPage);

    if (currentPage > maxPages) {
      store.commit("setPage", 1);
    }
  }
);

const apps = document.querySelectorAll(".vue-app");

Array.prototype.forEach.call(
  apps,
  (el) =>
    new Vue({
      el,
      template: el.innerHTML,
      store,
    })
);
